import { Schema as S } from "effect";

/**
 * CreateScheduleRequest schema for the DocCirrus schedule API.
 *
 * Per the DC API docs, only `start` and `calendar` are required fields.
 * All other fields are optional.
 *
 * @since 1.0.0
 */
export const CreateScheduleRequestSchema = S.Struct({
  /** Start date/time (required) */
  start: S.String,
  /** End date/time */
  end: S.optional(S.String),
  /** Doctor start time */
  doctorStart: S.optional(S.String),
  /** Doctor end time */
  doctorEnd: S.optional(S.String),
  /** Schedule type */
  type: S.optional(
    S.Union(
      [S.Literal("AVAILABLE"),
      S.Literal("UNAVAILABLE"),
      S.Literal("RESERVED"),
      S.Literal("BOOKED"),
      S.Literal("BLOCKED"),
      S.Literal("INFORMAL")],
    ),
  ),
  /** Title/description (read-only in DC) */
  title: S.optional(S.String),
  /** User-facing description */
  userDescr: S.optional(S.String),
  /** Urgency level (0-3) */
  urgency: S.optional(
    S.Union([S.Literal(0), S.Literal(1), S.Literal(2), S.Literal(3)]),
  ),
  /** Severity level */
  severity: S.optional(
    S.Union(
      [S.Literal("NONE"),
      S.Literal("LOW"),
      S.Literal("NORMAL"),
      S.Literal("IMPORTANT"),
      S.Literal("VERYIMPORTANT"),
      S.Literal("CRITICAL")],
    ),
  ),
  /** Additional details */
  details: S.optional(S.String),
  /** Estimated time of arrival */
  eta: S.optional(S.String),
  /** Push notification time */
  pushtime: S.optional(S.String),
  /** Call time */
  calltime: S.optional(S.String),
  /** Alert time */
  alertTime: S.optional(S.String),
  /** Whether this is an ad-hoc appointment */
  adhoc: S.optional(S.Boolean),
  /** Whether this is a group appointment */
  group: S.optional(S.Boolean),
  /** Close time flag */
  closeTime: S.optional(S.Boolean),
  /** Close day type */
  closeDayType: S.optional(
    S.Union(
      [S.Literal("HOLIDAY"),
      S.Literal("VACATION"),
      S.Literal("OTHER")],
    ),
  ),
  /** Color value for UI */
  colorValue: S.optional(S.String),
  /** Calendar ID (required per DC API docs, but ad-hoc appointments may omit it) */
  calendar: S.optional(S.String),
  /** Schedule type ID */
  scheduletype: S.optional(S.String),
  /** Populated schedule type */
  scheduletypePopulated: S.optional(S.String),
  /** All-day event flag */
  allDay: S.optional(S.Boolean),
  /** Duration in minutes */
  duration: S.optional(S.Number),
  /** Planned duration in minutes */
  plannedDuration: S.optional(S.Number),
  /** Appointment number */
  number: S.optional(S.Number),
  /** Employee/doctor ID */
  employee: S.optional(S.String),
  /** Patient ID */
  patient: S.optional(S.String),
  /** External resource reference */
  externalResource: S.optional(S.String),
  /** Author of the schedule entry */
  author: S.optional(S.String),
  /** Scheduled status (-2=unconfirmed, -1=no-show, 0=default, 1=in progress, 2=completed) */
  scheduled: S.optional(S.Number),
  /** Whether patient was in treatment */
  wasInTreatment: S.optional(S.Boolean),
  /** URL reference */
  url: S.optional(S.String),
  /** Patient arrival time */
  arrivalTime: S.optional(S.String),
  /** Group appointment ID */
  groupId: S.optional(S.String),
  /** Group capacity */
  capacityOfGroup: S.optional(S.Number),
  /** Last editor name */
  lastEditor: S.optional(S.String),
  /** Last edited date */
  lastEditedDate: S.optional(S.String),
  /** Whether created from patient portal */
  isFromPortal: S.optional(S.Boolean),
  /** Actual waiting time in minutes */
  actualWaitingTimeMinutes: S.optional(S.Number),
  /** Video conference ID */
  conferenceId: S.optional(S.String),
  /** Read-only flag */
  isReadOnly: S.optional(S.Boolean),
  /** Room assignment ID */
  roomId: S.optional(S.String),
  /** Order within the room */
  orderInRoom: S.optional(S.Number),
  /** Resource-based scheduling flag */
  resourceBased: S.optional(S.Boolean),
  /** Resource identifier */
  resource: S.optional(S.String),
  /** Link-by-resource reference */
  linkByResource: S.optional(S.String),
  /** Partner calendar ID */
  partnerCalendarId: S.optional(S.String),
});

export type CreateScheduleRequest = S.Schema.Type<typeof CreateScheduleRequestSchema>;
